// src/pages/GiftCardPage.js
import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../context/AuthContext';
import GiftCardPaymentModal from '../components/wallet/GiftCardPaymentModal';
import { convertToPersianNumbers } from '../utils/PersianNumberUtils';

const SUGGESTED_AMOUNTS = [50000, 100000, 200000, 500000];
const GIFT_CARD_IMAGES = [
  { id: 1, url: 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/giftcard/1-1.png' },
  { id: 2, url: 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/giftcard/2-1.png' },
  { id: 3, url: 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/giftcard/3-1.png' },
  { id: 4, url: 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/giftcard/4-1.png' },
];

const GiftCardPage = () => {
  const { isLoggedIn, openAuthModal } = useAuthContext();
  const [selectedCard, setSelectedCard] = useState(null);
  const [amount, setAmount] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [giftCard, setGiftCard] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (giftCard?.share_uuid) {
      console.log('✅ giftCard ذخیره شد، share_uuid:', giftCard.share_uuid);
    }
  }, [giftCard]);

  const handleSelectCard = (card) => {
    setSelectedCard(card);
  };

  const handleAmountSelect = (val) => {
    setAmount(val.toString());
  };

  const handleOpenPayment = () => {
    if (!isLoggedIn) {
      openAuthModal();
      return;
    }
    if (!selectedCard) {
      alert('لطفاً یک طرح کارت هدیه انتخاب کنید.');
      return;
    }
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      alert('لطفاً مبلغ معتبر انتخاب یا وارد کنید.');
      return;
    }
    setIsModalOpen(true);
  };

  const handleSuccess = (cardData) => {
    console.log('داده دریافتی از سرور:', cardData);
    if (!cardData.share_uuid) {
      console.error('share_uuid در پاسخ سرور وجود ندارد!');
      alert('خطا در دریافت اطلاعات کارت هدیه. لطفاً دوباره تلاش کنید.');
      return;
    }
    setGiftCard(cardData);
    setSelectedCard(null);
    setAmount('');
    setCustomMessage('');
    setIsModalOpen(false);
  };

  // ساخت لینک عمومی صفحه کارت هدیه (روی دامنه فرانت‌اند)
  const getPublicLink = () => {
    if (!giftCard?.share_uuid) return '';
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'https://app.farzaneh.app';
    return `${frontendUrl}/giftcard/${giftCard.share_uuid}/`;
  };

  // Fallback برای کپی در محیط‌های غیر HTTPS
  const fallbackCopyText = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  // کپی لینک عمومی
  const copyPublicLink = async () => {
    const link = getPublicLink();
    if (!link) {
      alert('لینکی برای کپی وجود ندارد. ممکن است اطلاعات کارت کامل نباشد.');
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      alert('لینک کارت هدیه کپی شد.');
    } catch (err) {
      console.warn('Clipboard API failed, using fallback', err);
      fallbackCopyText(link);
      alert('لینک کارت هدیه با روش جایگزین کپی شد.');
    }
  };

  // اشتراک‌گذاری (تصویر + متن + لینک)
  const handleShare = async () => {
    if (!giftCard) return;

    const amountPersian = convertToPersianNumbers(
      (giftCard.amount || 0).toLocaleString()
    );
    const shareTitle = 'کارت هدیه فرزانه';
    const shareText = `مبلغ: ${amountPersian} تومان\nپیام: ${giftCard.message || 'بدون پیام'}\nکد: ${giftCard.code}`;
    const link = getPublicLink();

    try {
      if (giftCard.image_url) {
        const response = await fetch(giftCard.image_url);
        const blob = await response.blob();
        const imageFile = new File([blob], 'giftcard.png', { type: 'image/png' });

        if (navigator.canShare && navigator.canShare({ files: [imageFile] })) {
          await navigator.share({
            title: shareTitle,
            text: shareText,
            files: [imageFile],
          });
        } else if (navigator.share) {
          await navigator.share({
            title: shareTitle,
            text: shareText,
          });
        } else {
          const fullText = `${shareTitle}\n${shareText}\nلینک: ${link}`;
          try {
            await navigator.clipboard.writeText(fullText);
            alert('متن و لینک کارت هدیه کپی شد. تصویر را جداگانه ذخیره کنید.');
          } catch (clipErr) {
            fallbackCopyText(fullText);
            alert('متن و لینک با روش جایگزین کپی شد. تصویر را جداگانه ذخیره کنید.');
          }
        }
      } else {
        throw new Error('آدرس تصویر موجود نیست');
      }
    } catch (err) {
      console.error(err);
      const fullText = `${shareTitle}\n${shareText}\nلینک: ${link}`;
      try {
        await navigator.clipboard.writeText(fullText);
        alert('متن و لینک کپی شد. تصویر را جداگانه ذخیره کنید.');
      } catch (clipErr) {
        fallbackCopyText(fullText);
        alert('متن و لینک با روش جایگزین کپی شد. تصویر را جداگانه ذخیره کنید.');
      }
    }
  };

  // کپی متن و لینک (بدون تصویر)
  const handleCopy = async () => {
    if (!giftCard) return;
    const amountPersian = convertToPersianNumbers(
      (giftCard.amount || 0).toLocaleString()
    );
    const shareTitle = 'کارت هدیه فرزانه';
    const shareText = `مبلغ: ${amountPersian} تومان\nپیام: ${giftCard.message || 'بدون پیام'}\nکد: ${giftCard.code}`;
    const fullText = `${shareTitle}\n${shareText}\nلینک: ${getPublicLink()}`;

    try {
      await navigator.clipboard.writeText(fullText);
      alert('متن و لینک کارت هدیه کپی شد.');
    } catch (err) {
      fallbackCopyText(fullText);
      alert('متن و لینک با روش جایگزین کپی شد.');
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <p className="text-gray-600 text-sm mb-4">برای دریافت کارت هدیه، لطفاً وارد شوید.</p>
        <button onClick={openAuthModal} className="bg-purple-600 text-white px-6 py-2 rounded-lg text-sm">
          ورود / ثبت‌نام
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-3 rtl">
      <div className="max-w-md mx-auto">
        {!giftCard ? (
          <>
            {/* متن راهنما با بولت پوینت */}
            <div className="mb-6 text-[12px] text-right bg-white rounded-xl p-4">
              <h1 className="text-[14px] font-extrabold">کــارت هـــدیه</h1>
            </div>

            {/* انتخاب طرح - کراسل افقی */}
            <div className="mb-4">
              <h2 className="text-[12px] font-thin">طرح مورد نظرتان را انتخاب کنید:</h2>
              <div className="flex overflow-x-auto gap-4 pb-2 scrollbar-thin">
                {GIFT_CARD_IMAGES.map((card) => (
                  <div
                    key={card.id}
                    onClick={() => handleSelectCard(card)}
                    className={`flex-shrink-0 w-64 cursor-pointer rounded-2xl overflow-hidden border transition-all duration-200 ${
                      selectedCard?.id === card.id
                        ? 'border-purple-600 shadow-lg ring-2 ring-purple-200'
                        : 'border-gray-200 shadow-md hover:shadow-lg'
                    }`}
                  >
                    <img
                      src={card.url}
                      alt="طرح کارت هدیه"
                      className="w-full h-auto"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden w-full h-32 bg-gray-200 flex items-center justify-center text-gray-500 text-xs">
                      خطا در بارگذاری تصویر
                    </div>
                    {selectedCard?.id === card.id && (
                      <div className="text-center py-1 bg-purple-100 text-purple-700 text-xs font-bold">
                        انتخاب شده
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* انتخاب مبلغ */}
            <div className="bg-white rounded-2xl shadow-md p-4 mb-4">
              <label className="text-sm font-medium text-gray-700 block mb-2">مبلغ (تومان)</label>
              <input
                type="number"
                placeholder="مثلاً ۱۰۰۰۰۰"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-2 text-sm text-left focus:ring-purple-500 focus:border-purple-500"
              />
              <div className="flex flex-wrap gap-2 mt-3">
                {SUGGESTED_AMOUNTS.map((val) => (
                  <button
                    key={val}
                    onClick={() => handleAmountSelect(val)}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs px-4 py-1.5 rounded-full transition"
                  >
                    {convertToPersianNumbers(val.toLocaleString())} تومان
                  </button>
                ))}
              </div>
            </div>

            {/* متن دلخواه */}
            <div className="bg-white rounded-2xl shadow-md p-4 mb-6">
              <label className="text-sm font-medium text-gray-700 block mb-2">متن دلخواه (اختیاری)</label>
              <textarea
                rows="3"
                placeholder="پیام خود را برای گیرنده بنویسید..."
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-2 text-sm focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            <button
              onClick={handleOpenPayment}
              className="w-full bg-violet-800 hover:bg-purple-700 text-white font-medium py-3 rounded-xl transition duration-200 shadow-md"
            >
             کپی کد هدیه
            </button>
          </>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
            <div className="w-full h-auto mb-4 rounded-xl overflow-hidden shadow">
              {giftCard.image_url ? (
                <img
                  src={giftCard.image_url}
                  alt="کارت هدیه"
                  className="w-full h-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="hidden w-full h-40 bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
                تصویر کارت در دسترس نیست
              </div>
            </div>
            <p className="text-2xl font-bold text-purple-700 mb-2">
              {convertToPersianNumbers((giftCard.amount || 0).toLocaleString())} تومان
            </p>
            {giftCard.message && (
              <div className="bg-gray-50 p-3 rounded-xl my-3 text-gray-600 text-sm italic border-r-4 border-purple-300">
                "{giftCard.message}"
              </div>
            )}
            <div className="bg-gray-100 rounded-xl p-2 text-sm tracking-widest font-mono text-gray-800 my-3 break-all">
              کد کارت: {giftCard.code}
            </div>

            <div className="bg-purple-50 rounded-xl p-3 my-3 text-right">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="text-purple-700 font-medium">لینک هدیه:</span>
                <button
                  onClick={copyPublicLink}
                  className="text-purple-600 hover:text-purple-800 bg-white px-2 py-1 rounded-lg text-xs shadow"
                >
                  کپی لینک
                </button>
              </div>
              <div className="mt-1 text-xs text-gray-600 break-all bg-white p-2 rounded-lg">
                {getPublicLink() || 'در حال ساخت...'}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                این لینک را برای گیرنده ارسال کنید. با باز کردن لینک، کارت هدیه قابل مشاهده و دریافت است.
              </p>
            </div>
            <button
              onClick={() => setGiftCard(null)}
              className="text-purple-600 mt-5 text-sm underline hover:no-underline"
            >
              + ساخت کارت هدیه جدید
            </button>
          </div>
        )}
      </div>

      <GiftCardPaymentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleSuccess}
        amount={amount}
        selectedCard={selectedCard}
        customMessage={customMessage}
      />
    </div>
  );
};

export default GiftCardPage;